/**
 * KAN-887, KAN-891, KAN-892, KAN-898: быстрый выбор таблицы CRM ("Продажи",
 * "Неразобранные", "Нет движения", "Мусор (N)") со счётчиками из ответа таблицы.
 * hidden - пункты, недоступные пользователю ("Мусор" только владельцу). Одна активна,
 * адрес и переключение - lib/crmTableShortcuts (через useCrmTablePeriod),
 * компонент только рисует.
 * KAN-906: одна группа-переключатель (SegmentedControl) с первым пунктом "Все" = ни один
 * быстрый фильтр не выбран; "Все" отжимает активный пункт тем же onToggle.
 */
import { SegmentedControl, type SegmentedOption } from '@/components/crm/board/SegmentedControl';
import type { CrmTableShortcut } from '@/lib/crmTableShortcuts';

interface ShortcutDef {
  id: CrmTableShortcut;
  label: string;
  /** Счётчик в подписи: "Мусор (N)" вместо значка. */
  countInLabel?: boolean;
  /** KAN-907: подсказка при наведении. */
  hint?: string;
}

/** KAN-907: подсказка пункта "Нет движения". */
export const IDLE_SHORTCUT_HINT =
  'Открытые лиды с менеджером или взятые в работу: 7 дней нет сообщений, звонков и назначенного перезвона. Такие лиды возвращаются в Требуют внимания';

const SHORTCUTS: readonly ShortcutDef[] = [
  { id: 'sales', label: 'Продажи' },
  { id: 'unassigned', label: 'Неразобранные' },
  { id: 'idle', label: 'Нет движения', hint: IDLE_SHORTCUT_HINT },
  { id: 'junk', label: 'Мусор', countInLabel: true },
];

/** Значение пункта "Все". */
const ALL = 'all';
type ShortcutValue = CrmTableShortcut | typeof ALL;

interface CrmTableShortcutsProps {
  active: CrmTableShortcut | null;
  counts: Partial<Record<CrmTableShortcut, number | undefined>>;
  hidden?: readonly CrmTableShortcut[];
  onToggle: (shortcut: CrmTableShortcut) => void;
}

export function CrmTableShortcuts({ active, counts, hidden = [], onToggle }: CrmTableShortcutsProps): JSX.Element {
  const options: SegmentedOption<ShortcutValue>[] = [
    { value: ALL, label: 'Все', testId: 'crm-all-shortcut' },
    ...SHORTCUTS.filter((s) => !hidden.includes(s.id)).map((s): SegmentedOption<ShortcutValue> => {
      const count = counts[s.id];
      return {
        value: s.id,
        label: s.countInLabel ? `${s.label} (${count ?? 0})` : s.label,
        title: s.hint,
        testId: `crm-${s.id}-shortcut`,
        badge:
          !s.countInLabel && typeof count === 'number' ? (
            <span className="rounded-full bg-gray-200/70 px-1.5 text-xs tabular-nums text-gray-600">{count}</span>
          ) : undefined,
      };
    }),
  ];

  const handleChange = (value: ShortcutValue) => {
    if (value === ALL) {
      if (active) onToggle(active);
      return;
    }
    // Повторное нажатие активного пункта отжимает его (как раньше), переключение - тот же onToggle.
    onToggle(value);
  };

  return (
    <SegmentedControl
      label="Быстрые фильтры"
      options={options}
      value={active ?? ALL}
      onChange={handleChange}
      testId="crm-table-shortcuts"
      compact
      scrollable
    />
  );
}
